import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { subscribeToProgress } from '../../utils/actionCableSubscriptions';
import { fetchSubtitles } from '../../features/subtitles/subtitleSlice';

const MAX_BETA_VIDEO_DURATION_SECONDS = 60;

const useVideoProcessing = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalVideoURL, setOriginalVideoURL] = useState(null);
  const [processedVideoURL, setProcessedVideoURL] = useState(null);
  const [subtitlesURL, setSubtitlesURL] = useState(null);
  const [downloadToken, setDownloadToken] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingFinished, setIsProcessingFinished] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [buttonText, setButtonText] = useState('Download');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeModalTitle, setSubscribeModalTitle] = useState(
    "You've used today's Free upload"
  );
  const [subscribeModalMessage, setSubscribeModalMessage] = useState(
    'Upgrade to Starter for 25 videos per day at $4.99/month.'
  );
  const [fileInputResetKey, setFileInputResetKey] = useState(0);
  const [videoValidationError, setVideoValidationError] = useState('');
  const [selectedVideoDuration, setSelectedVideoDuration] = useState(null);

  const originalVideoRef = useRef(null);
  const processedVideoRef = useRef(null);
  const progressSubscriptionRef = useRef(null);

  const dispatch = useDispatch();
  const csrfToken = localStorage.getItem('csrfToken');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const getApiBaseUrl = useCallback(() => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    return baseUrl.replace(/\/$/, '');
  }, []);

  const buildApiUrl = useCallback((path) => {
    const cleanBaseUrl = getApiBaseUrl();
    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    return `${cleanBaseUrl}${cleanPath}`;
  }, [getApiBaseUrl]);

  const isSubscriptionLimitError = useCallback((message = '') => {
    const normalizedMessage = String(message).toLowerCase();

    return (
      normalizedMessage.includes('free plan') ||
      normalizedMessage.includes('starter') ||
      normalizedMessage.includes('upgrade') ||
      normalizedMessage.includes('upload limit') ||
      normalizedMessage.includes('daily upload') ||
      normalizedMessage.includes('used today')
    );
  }, []);

  const openSubscribeModal = useCallback((message) => {
    setSubscribeModalTitle("You've used today's Free upload");
    setSubscribeModalMessage(
      message || 'Upgrade to Starter for 25 videos per day at $4.99/month.'
    );
    setShowSubscribeModal(true);
  }, []);

  const checkUploadUsageBeforeUpload = useCallback(async () => {
    const response = await fetch(buildApiUrl('/api/v1/home/upload_usage'), {
      method: 'GET',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
    });

    let data = {};

    try {
      data = await response.json();
    } catch (jsonError) {
      console.error('Unable to parse upload usage response:', jsonError);
    }

    if (!response.ok) {
      const error = new Error(
        data.error || 'Please sign in or upgrade before uploading.'
      );

      error.status = response.status;
      error.usage = data.usage;

      throw error;
    }

    if (!data?.usage?.upload_available_today) {
      const subscriptionLabel = String(
        data?.usage?.subscription_label || data?.usage?.subscription_tier || 'Free'
      ).toLowerCase();

      const error = new Error(
        subscriptionLabel === 'free'
          ? "You’ve used today’s Free plan upload. Upgrade to Starter for 25 videos per day."
          : 'You’ve reached today’s upload limit. Upgrade for more capacity.'
      );

      error.status = 402;
      error.usage = data.usage;

      throw error;
    }

    return data.usage;
  }, [buildApiUrl, csrfToken]);

  const handleSubscribeClick = useCallback(async () => {
    try {
      setVideoValidationError('');
      setSubscribeModalTitle('Opening Starter Checkout');
      setSubscribeModalMessage(
        'Opening secure Stripe Checkout for Starter: 25 videos per day at $4.99/month.'
      );

      const response = await fetch(buildApiUrl('/api/v1/stripe/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      let data = {};

      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Unable to parse Stripe checkout response:', jsonError);
      }

      if (!response.ok) {
        throw new Error(data.error || 'Unable to start checkout. Please try again.');
      }

      if (!data.checkout_url) {
        throw new Error('Stripe checkout did not return a checkout URL.');
      }

      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Stripe checkout error:', error);

      setSubscribeModalTitle('Checkout could not start');
      setSubscribeModalMessage(
        error.message || 'Unable to start checkout. Please try again.'
      );
      setShowSubscribeModal(true);
    }
  }, [buildApiUrl, csrfToken]);

  const formatVideoDuration = useCallback((seconds) => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
      return '0:00';
    }

    const roundedSeconds = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(roundedSeconds / 60);
    const remainingSeconds = roundedSeconds % 60;

    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }, []);

  const validateVideoDuration = useCallback((file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('Please select a video file.'));
        return;
      }

      if (!file.type.startsWith('video/')) {
        reject(new Error('Please select a valid video file.'));
        return;
      }

      const metadataVideo = document.createElement('video');
      const objectUrl = URL.createObjectURL(file);

      metadataVideo.preload = 'metadata';
      metadataVideo.muted = true;
      metadataVideo.playsInline = true;

      const cleanup = () => {
        metadataVideo.removeAttribute('src');
        metadataVideo.load();
        URL.revokeObjectURL(objectUrl);
      };

      metadataVideo.onloadedmetadata = () => {
        const duration = metadataVideo.duration;
        cleanup();

        if (!Number.isFinite(duration) || duration <= 0) {
          reject(new Error('We could not read the video length. Please try another video file.'));
          return;
        }

        resolve(duration);
      };

      metadataVideo.onerror = () => {
        cleanup();
        reject(new Error('We could not read this video file. Please try a different video.'));
      };

      metadataVideo.src = objectUrl;
    });
  }, []);

  const stopProgressSubscription = useCallback(() => {
    if (progressSubscriptionRef.current) {
      progressSubscriptionRef.current.unsubscribe();
      progressSubscriptionRef.current = null;
      console.log('ActionCable progress subscription stopped.');
    }
  }, []);

  const resetProcessingState = useCallback(() => {
    setProcessedVideoURL(null);
    setSubtitlesURL(null);
    setDownloadToken(null);
    setLoadingSteps([]);
    setIsUploading(false);
    setIsProcessingFinished(false);
    setCountdown(null);
    setButtonText('Download');
    setShowDownloadModal(false);
  }, []);

  const handleClearSelectedFile = useCallback(() => {
    stopProgressSubscription();

    if (originalVideoURL && originalVideoURL.startsWith('blob:')) {
      URL.revokeObjectURL(originalVideoURL);
    }

    setSelectedFile(null);
    setOriginalVideoURL(null);
    setVideoValidationError('');
    setSelectedVideoDuration(null);
    resetProcessingState();

    setFileInputResetKey((previousKey) => previousKey + 1);
  }, [originalVideoURL, resetProcessingState, stopProgressSubscription]);

  const handleDeleteVideo = useCallback(async () => {
    if (!downloadToken) return;

    try {
      const response = await fetch(buildApiUrl(`/api/v1/home/delete_video?token=${downloadToken}`), {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Error deleting file. Please try again.';

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          console.error('Unable to parse delete error response:', jsonError);
        }

        if (response.status === 404) {
          console.log('Video already deleted.');
          return;
        }

        throw new Error(errorMessage);
      }

      setButtonText('Video removed for system optimization');
      setCountdown(null);
      setDownloadToken(null);
      stopProgressSubscription();
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  }, [buildApiUrl, csrfToken, downloadToken, stopProgressSubscription]);

  useEffect(() => {
    let timer;

    if (countdown !== null && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((previousCountdown) => previousCountdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      handleDeleteVideo();
    }

    return () => clearInterval(timer);
  }, [countdown, handleDeleteVideo]);

  useEffect(() => {
    if (downloadToken) {
      stopProgressSubscription();

      console.log('Subscribing to channel with downloadToken:', downloadToken);

      const subscription = subscribeToProgress(downloadToken, (data) => {
        console.log('Received data from channel:', data);

        if (data.status === 'Error') {
          const errorText = data.message
            ? `❌ Error: ${data.message}`
            : '❌ Error encountered.';

          setLoadingSteps((previousSteps) => [...previousSteps, errorText]);
          setIsUploading(false);
          setIsProcessingFinished(false);
          stopProgressSubscription();

          return;
        }

        if (data.status) {
          setLoadingSteps((previousSteps) => [...previousSteps, `✔️ ${data.status}`]);
        }

        if (data.status === 'Finished processing') {
          const previewUrl = buildApiUrl(`/api/v1/home/preview_video?token=${downloadToken}`);

          console.log('Final processed video preview URL:', previewUrl);
          console.log('Raw S3 result URL from ActionCable:', data.result_url);

          setSubtitlesURL(previewUrl);
          setProcessedVideoURL(previewUrl);
          setIsUploading(false);
          setIsProcessingFinished(true);
          setCountdown(600);

          dispatch(fetchSubtitles(downloadToken));

          stopProgressSubscription();
        }
      });

      progressSubscriptionRef.current = subscription;

      return () => {
        stopProgressSubscription();
      };
    }

    return () => {
      stopProgressSubscription();
    };
  }, [
    buildApiUrl,
    dispatch,
    downloadToken,
    stopProgressSubscription,
  ]);

  useEffect(() => {
    const handleUnload = () => {
      stopProgressSubscription();

      if (downloadToken) {
        handleDeleteVideo();
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      stopProgressSubscription();
    };
  }, [downloadToken, handleDeleteVideo, stopProgressSubscription]);

  useEffect(() => {
    return () => {
      if (originalVideoURL && originalVideoURL.startsWith('blob:')) {
        URL.revokeObjectURL(originalVideoURL);
      }
    };
  }, [originalVideoURL]);

  const toggleFullscreen = useCallback((videoRef) => {
    if (!isIOS && document.fullscreenElement) {
      document.exitFullscreen().catch((error) => {
        console.error('Failed to exit fullscreen', error);
      });
    } else if (!isIOS) {
      videoRef.current?.requestFullscreen().catch((error) => {
        console.error('Failed to enter fullscreen', error);
      });
    } else {
      console.log('Fullscreen disabled for iOS.');
    }
  }, [isIOS]);

  const formatCountdown = useCallback(() => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }, [countdown]);

  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files[0];

    setVideoValidationError('');
    setSelectedVideoDuration(null);

    if (!file) {
      event.target.value = '';
      return;
    }

    if (!file.type.startsWith('video/')) {
      event.target.value = '';
      setVideoValidationError('Please select a valid video file.');
      alert('Please select a valid video file.');
      return;
    }

    try {
      const duration = await validateVideoDuration(file);

      stopProgressSubscription();

      if (originalVideoURL && originalVideoURL.startsWith('blob:')) {
        URL.revokeObjectURL(originalVideoURL);
      }

      const newOriginalVideoURL = URL.createObjectURL(file);

      console.log('File selected, setting original video URL:', newOriginalVideoURL);
      console.log('Selected video duration:', duration);

      setSelectedFile(file);
      setOriginalVideoURL(newOriginalVideoURL);
      setSelectedVideoDuration(duration);
      setVideoValidationError('');
      resetProcessingState();
    } catch (error) {
      event.target.value = '';
      setSelectedFile(null);
      setSelectedVideoDuration(null);
      setVideoValidationError(error.message);

      if (originalVideoURL && originalVideoURL.startsWith('blob:')) {
        URL.revokeObjectURL(originalVideoURL);
      }

      setOriginalVideoURL(null);
      resetProcessingState();
      alert(error.message);
    }
  }, [
    originalVideoURL,
    resetProcessingState,
    stopProgressSubscription,
    validateVideoDuration,
  ]);

  const handleUploadAndGenerateSubtitles = useCallback(async ({ rightsConfirmed } = {}) => {
    if (!selectedFile) {
      setVideoValidationError('Please select a video first.');
      alert('Please select a video first.');
      return;
    }

    if (!rightsConfirmed) {
      setVideoValidationError(
        'Please confirm that you own this video or have permission to process it.'
      );
      return;
    }

    try {
      await validateVideoDuration(selectedFile);
    } catch (error) {
      setVideoValidationError(error.message);
      alert(error.message);
      return;
    }

    stopProgressSubscription();

    setIsUploading(true);
    setIsProcessingFinished(false);
    setLoadingSteps([]);
    setShowDownloadModal(false);
    setShowSubscribeModal(false);
    setProcessedVideoURL(null);
    setSubtitlesURL(null);
    setDownloadToken(null);
    setVideoValidationError('');

    try {
      await checkUploadUsageBeforeUpload();
    } catch (error) {
      console.error('Upload usage preflight blocked upload:', error);

      setIsUploading(false);
      setDownloadToken(null);
      stopProgressSubscription();

      if (error.status === 401) {
        setVideoValidationError(error.message);
        alert(error.message);
        return;
      }

      setLoadingSteps([]);
      setIsProcessingFinished(false);
      setProcessedVideoURL(null);
      setSubtitlesURL(null);
      openSubscribeModal(error.message);
      return;
    }

    const formData = new FormData();
    formData.append('video', selectedFile);
    formData.append('rights_confirmed', rightsConfirmed ? 'true' : 'false');

    try {
      const response = await fetch(buildApiUrl('/api/v1/home/upload_and_generate_subtitles'), {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Error uploading file. Please try again.';

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (jsonError) {
          console.error('Unable to parse upload error response:', jsonError);
        }

        const error = new Error(errorMessage);
        error.status = response.status;

        throw error;
      }

      const data = await response.json();

      setDownloadToken(data.download_token);
    } catch (error) {
      console.error('Error during upload and processing:', error);

      setIsUploading(false);
      setDownloadToken(null);
      stopProgressSubscription();

      if (isSubscriptionLimitError(error.message)) {
        setLoadingSteps([]);
        setIsProcessingFinished(false);
        setProcessedVideoURL(null);
        setSubtitlesURL(null);
        openSubscribeModal(error.message);
        return;
      }

      alert(error.message);
      setLoadingSteps(['❌ Error encountered.']);
    }
  }, [
    buildApiUrl,
    checkUploadUsageBeforeUpload,
    csrfToken,
    isSubscriptionLimitError,
    openSubscribeModal,
    selectedFile,
    stopProgressSubscription,
    validateVideoDuration,
  ]);

  const handleDownloadVideo = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }

    if (!downloadToken) {
      alert('No video available for download.');
      return;
    }

    const downloadUrl = buildApiUrl(`/api/v1/home/download_video?token=${downloadToken}`);
    const anchor = document.createElement('a');

    anchor.href = downloadUrl;
    anchor.download = 'processed_video.mp4';
    anchor.rel = 'noopener';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setShowDownloadModal(true);
  }, [buildApiUrl, downloadToken]);

  return {
    selectedFile,
    originalVideoURL,
    processedVideoURL,
    subtitlesURL,
    downloadToken,
    loadingSteps,
    isUploading,
    isProcessingFinished,
    countdown,
    buttonText,
    showDownloadModal,
    setShowDownloadModal,
    showSubscribeModal,
    setShowSubscribeModal,
    subscribeModalTitle,
    subscribeModalMessage,
    handleSubscribeClick,
    fileInputResetKey,
    originalVideoRef,
    processedVideoRef,
    videoValidationError,
    selectedVideoDuration,
    maxBetaVideoDurationSeconds: MAX_BETA_VIDEO_DURATION_SECONDS,
    formatVideoDuration,
    handleFileChange,
    handleClearSelectedFile,
    handleUploadAndGenerateSubtitles,
    handleDownloadVideo,
    handleDeleteVideo,
    toggleFullscreen,
    formatCountdown,
  };
};

export default useVideoProcessing;